'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

type ConnectionStatus = 'connected' | 'degraded' | 'disconnected';

interface SupabaseStatusDotProps {
  /** Show the escalations-updated-X-seconds-ago label */
  showEscalationsAge?: boolean;
  /** Show the alert count badge */
  alertCount?: number;
  /** Extra className for the wrapper */
  className?: string;
}

const STATUS_CONFIG: Record<ConnectionStatus, { color: string; label: string; pulse: boolean }> = {
  connected:    { color: '#22c55e', label: 'Live',      pulse: true  },
  degraded:     { color: '#f59e0b', label: 'Degraded',  pulse: true  },
  disconnected: { color: '#ef4444', label: 'Offline',   pulse: false },
};

export default function SupabaseStatusDot({
  showEscalationsAge = true,
  alertCount,
  className = '',
}: SupabaseStatusDotProps) {
  const supabase = createClient();

  const [status, setStatus] = useState<ConnectionStatus>('connected');
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  // ── Probe Supabase connection health ──────────────────────────────────────
  const probeConnection = useCallback(async () => {
    const start = Date.now();
    try {
      const { error } = await supabase
        .from('escalations')
        .select('updated_at')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const elapsed = Date.now() - start;

      if (error) {
        setStatus('disconnected');
      } else if (elapsed > 3000) {
        setStatus('degraded');
      } else {
        setStatus('connected');
      }
      setLastFetchedAt(new Date());
    } catch {
      setStatus('disconnected');
    }
  }, [supabase]);

  // Probe on mount and every 30 s
  useEffect(() => {
    probeConnection();
    const interval = setInterval(probeConnection, 30_000);
    return () => clearInterval(interval);
  }, [probeConnection]);

  // ── Tick seconds-ago counter every second ─────────────────────────────────
  useEffect(() => {
    if (!lastFetchedAt) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - lastFetchedAt.getTime()) / 1000);
      setSecondsAgo(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastFetchedAt]);

  const cfg = STATUS_CONFIG[status];

  const secondsLabel =
    secondsAgo === null
      ? null
      : secondsAgo < 5
      ? 'just now'
      : `${secondsAgo}s ago`;

  return (
    <div className={`flex items-center gap-2 select-none ${className}`}>
      {/* Status dot */}
      <span className="relative flex items-center justify-center w-4 h-4 flex-shrink-0">
        {cfg.pulse && (
          <span
            className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping"
            style={{ backgroundColor: cfg.color }}
          />
        )}
        <span
          className="relative inline-flex rounded-full w-2.5 h-2.5"
          style={{ backgroundColor: cfg.color }}
        />
      </span>

      {/* Labels */}
      <div className="flex items-center gap-1.5 text-[11px] leading-none text-gray-500 font-medium">
        <span style={{ color: cfg.color }} className="font-semibold">{cfg.label}</span>

        {showEscalationsAge && secondsLabel && (
          <>
            <span className="text-gray-300">·</span>
            <span>escalations {secondsLabel}</span>
          </>
        )}

        {typeof alertCount === 'number' && (
          <>
            <span className="text-gray-300">·</span>
            <span
              className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: alertCount > 0 ? '#ef4444' : '#9ca3af', minWidth: 18 }}
            >
              {alertCount}
            </span>
            <span>alerts</span>
          </>
        )}
      </div>
    </div>
  );
}
